import React, { type CSSProperties, type ReactNode } from 'react';
import {
    Button,
    FilterPopover,
    useOverflowCollapse,
    type FilterPopoverTriggerState,
} from '@/components/ui';
import { AuditSearchInput } from './AuditSearchInput';
import { ChevronDownIcon, CrossIcon, FunnelIcon } from './icons';
import styles from './AuditFilters.module.css';

export interface AuditFilterBarProps {
    /**
     * Grouped because they always travel together — see CLAUDE.md's prop-count
     * rule. `clearLabel` is the ✕ button's accessible name: the button is
     * icon-only, and without it the name fell back to the whole placeholder
     * ("Tìm theo người thao tác, đối tượng, mã hành động…"), which is a
     * description of the FIELD and not of what the button does.
     */
    search: {
        value: string;
        onChange: (next: string) => void;
        placeholder: string;
        clearLabel?: string;
    };
    /** The facet chips, in the order the caller wants them. Composition, not a config array. */
    chips: ReactNode;
    /** How many facets are currently narrowing the table. Drives both the count chip and "Xoá bộ lọc". */
    activeCount: number;
    onClear: () => void;
    shown: number;
    total: number;
    /** All user-facing copy. This component holds none of its own. */
    labels: { filters: string; clear: string; showing: string };
    className?: string;
    style?: CSSProperties;
}

/**
 * EAA-1821: the Nhật ký filter bar shell — search, the caller's chips, a count
 * readout, a clear action, and the result counter pushed to the far end.
 *
 * It takes `chips` as a NODE rather than a descriptor array on purpose: the
 * audit screen has two panels (tài khoản / quản trị) whose facets differ, and a
 * descriptor array would have needed a discriminated union plus a renderer here
 * to express that — the composition boundary is where the difference already is.
 *
 * The count chip and the clear action only mount when something is actually
 * filtered. An always-visible "0 bộ lọc" and a permanently dead "Xoá bộ lọc"
 * are two controls that never do anything, which is exactly what trains an
 * operator to stop reading the bar.
 *
 * **The bar is ONE LINE at every width.** When the chips stop fitting they
 * collapse into a single "Bộ lọc" button that opens all of them together,
 * rather than wrapping onto a second row. Wrapping looked harmless but was not:
 * this page is fit-to-shell, so a second toolbar row is a row of log that no
 * longer fits, and it is the narrower viewports — the ones with least room to
 * spare — that paid for it. See `useOverflowCollapse` for why the width is
 * measured from the real chips and remembered, rather than from a hidden twin.
 */
export function AuditFilterBar({
    search,
    chips,
    activeCount,
    onClear,
    shown,
    total,
    labels,
    className,
    style,
}: AuditFilterBarProps): React.ReactElement {
    const hasFilters = activeCount > 0;
    const { containerRef, contentRef, collapsed } = useOverflowCollapse();

    const renderCollapsedTrigger = ({ open, ref, toggle }: FilterPopoverTriggerState) => (
        <Button
            ref={ref}
            className={hasFilters ? `${styles.chip} is-active` : styles.chip}
            // EAA-1846: `portal-audit-filter-more` carried no styling at all — it
            // existed only so the e2e suite could find this button. The suite asks
            // for it by test id now, which survives class hashing.
            data-testid="audit-filter-more"
            aria-haspopup="dialog"
            aria-expanded={open}
            onClick={toggle}
        >
            <FunnelIcon className={styles.chipIcon} />
            <span className={styles.chipLabel} data-testid="audit-filter-chip-label">
                {labels.filters}
            </span>
            {/* EAA-1821: collapsed, the badge counts ACTIVE filters rather than
                available options — the individual chips are the ones that
                advertise how much is on offer, and here there is no room to. */}
            {hasFilters && (
                <span className={styles.chipBadge} data-testid="audit-filter-chip-badge">
                    {activeCount}
                </span>
            )}
            <ChevronDownIcon className={styles.chipIcon} />
        </Button>
    );

    return (
        <div
            className={[styles.bar, className ?? ''].filter(Boolean).join(' ')}
            style={style}
            data-testid="audit-filter-bar"
        >
            <AuditSearchInput
                value={search.value}
                onChange={search.onChange}
                placeholder={search.placeholder}
                clearLabel={search.clearLabel}
            />

            {/* The budget slot: `flex: 1 1 auto; min-width: 0` in CSS, so its
                width is "what is left over" and does NOT change when the chips
                collapse — which is what keeps the decision from oscillating. */}
            <div className={styles.barSlot} ref={containerRef}>
                {collapsed ? (
                    <FilterPopover className={styles.chipRoot} trigger={renderCollapsedTrigger}>
                        <div className={styles.morePanel} data-testid="audit-filter-more-panel">
                            {chips}
                        </div>
                    </FilterPopover>
                ) : (
                    /* `flex: none` in CSS, so this row reports its NATURAL width
                       even in the frame where it no longer fits — that width is
                       what the hook remembers to decide when to expand again. */
                    <div className={styles.barChips} ref={contentRef}>
                        {chips}
                    </div>
                )}
            </div>

            {/* Collapsed, the trigger already carries the count — a second badge
                beside it would say the same number twice. */}
            {hasFilters && !collapsed && (
                <span className={styles.count} data-testid="audit-filter-count">
                    {labels.filters}
                    <span className={styles.countBadge} data-testid="audit-filter-count-badge">
                        {activeCount}
                    </span>
                </span>
            )}
            {hasFilters && (
                <Button className={styles.clear} onClick={onClear}>
                    <CrossIcon />
                    {labels.clear}
                </Button>
            )}
            <span className={styles.result} aria-live="polite" data-testid="audit-filter-result">
                {labels.showing} <strong className={styles.resultCount}>{shown}</strong> / {total}
            </span>
        </div>
    );
}
